/**
 * The prompt catalogue.
 *
 * A list, and only a list. Authoring, checking, previewing and releasing moved to
 * AdminPromptDetail - they need the room, and mixing "which prompts exist" with
 * "what will this one send" produced a screen where the save button sat two
 * scrolls below the thing it saved.
 *
 * What a row has to answer is whether this job is running on an instruction
 * anybody here wrote: a prompt with no saved version runs on the one compiled
 * into the server, which is safe but is not what the operator chose.
 *
 * CREATING ONE IS A SCREEN, NOT A FORM HERE. It was once a picker and a name
 * box on this page, which produced an empty prompt and a second step nobody was
 * told about - the instruction is the only thing that matters about a prompt, so
 * creating one is writing one. AdminPromptNew does that.
 *
 * A new prompt is always for a generation the code already calls (migration
 * 052's four). A new GENERATION is still a migration alongside the call site
 * that asks for it, because a prompt nothing asks for can never run.
 *
 * NOTHING HERE IS ABOUT A PARTICULAR CLINIC. Writing the instruction and
 * deciding who reads it are different decisions with different audiences: the
 * wording is written and edited here, and which clinic follows it is set on that
 * clinic's own page, next to the rest of its setup.
 */
import { useState } from 'react';
import { Link, useLoaderData, useRevalidator } from 'react-router';
import { ApiError, post, type AdminPrompt, type ListResponse } from '../api.ts';
import { Card, Empty, Pill, RowLink, Table, jaDate, jaNumber } from '../components/ui.tsx';

const TASK_LABEL: Record<string, string> = {
  outline: '構成案（タイトル・見出し）',
  proposals: 'タイトル・見出し案',
  section: '節ごとの執筆',
  body: '記事全体の執筆',
  rewrite: '部分リライト',
  compliance: '薬機法チェック',
  image: '画像生成',
  persona: 'ペルソナ',
};

export default function AdminPrompts() {
  const { items } = useLoaderData<ListResponse<AdminPrompt>>();
  const revalidator = useRevalidator();
  const unreleased = items.filter((prompt) => !prompt.active_version_no).length;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /*
   * Save the shipped wording for everything unsaved, in one action.
   *
   * Generation refuses without a saved instruction, so a fresh install has
   * every feature answering an error until somebody presses save on each of
   * eight screens. This is that, once - and still attributed: the versions it
   * writes name whoever pressed it, exactly as a hand-written save does. It
   * never touches a prompt that already has a live version.
   */
  async function seedDefaults() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await post<{ seeded: Array<{ key: string; versionNo: number }> }>(
        '/admin/prompts/seed-defaults',
        {},
      );
      setNotice(
        result.seeded.length === 0
          ? '保存が必要なプロンプトはありませんでした。'
          : `${result.seeded.length} 件を既定の指示文で保存しました。次の生成から使われます。`,
      );
      await revalidator.revalidate();
    } catch (err) {
      setError(
        err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : '保存できませんでした',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title="プロンプト管理"
      caption="AIに渡す指示文の管理です。行を選ぶと、編集・プレビュー・差分確認ができます。保存した時点で使われます。"
      actions={
        <>
          {unreleased > 0 && (
            <button
              className="btn btn--sm"
              type="button"
              disabled={busy}
              onClick={() => void seedDefaults()}
            >
              {busy ? '保存中…' : `未保存の ${unreleased} 件を既定値で保存`}
            </button>
          )}
          <Link className="btn btn--sm btn--ghost" to="/admin/prompts/new">
            新しいプロンプト
          </Link>
          {/*
           * The チェック項目 screen used to be its own sidebar entry. Moved here,
           * beside what it judges: it has nothing to say to anybody who has not
           * opened a prompt, and a JMC nav item for it every day is a nav item
           * that gets clicked once and then ignored.
           */}
          <Link className="btn btn--sm btn--ghost" to="/admin/prompt-checks">
            チェック項目の設定
          </Link>
        </>
      }
    >
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}
      {notice && !error && (
        <p className="muted small" role="status">
          {notice}
        </p>
      )}
      {items.length === 0 ? (
        <Empty>プロンプトはまだ登録されていません。</Empty>
      ) : (
        <>
          <Table
            head={
              <>
                <th scope="col">プロンプト名</th>
                <th scope="col">用途</th>
                <th scope="col">キー</th>
                <th scope="col">使用中</th>
                <th scope="col">下書き</th>
                <th scope="col">最終保存</th>
              </>
            }
          >
            {items.map((prompt) => (
              <tr key={prompt.id} className="row-link">
                <td>
                  <RowLink to={`/admin/prompts/${prompt.id}`}>{prompt.name}</RowLink>
                  {prompt.description && (
                    <span className="muted small" style={{ display: 'block' }}>
                      {prompt.description}
                    </span>
                  )}
                </td>
                <td>{TASK_LABEL[prompt.task_type] ?? prompt.task_type}</td>
                <td className="muted small">{prompt.key}</td>
                <td>
                  {prompt.active_version_no ? (
                    <Pill tone="ok">v{prompt.active_version_no}</Pill>
                  ) : (
                    <Pill tone="warn">未保存</Pill>
                  )}
                </td>
                <td>
                  {Number(prompt.draft_count ?? 0) > 0 ? (
                    <Pill tone="muted">{jaNumber(prompt.draft_count)} 件</Pill>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>{jaDate(prompt.activated_at)}</td>
              </tr>
            ))}
          </Table>

          {unreleased > 0 && (
            <p className="muted small" style={{ marginBottom: 0 }}>
              {jaNumber(unreleased)} 件のプロンプトは保存されたバージョンがありません。
              <strong>保存されるまで、その生成は実行されずエラーになります。</strong>
              指示文を確認してから1件ずつ保存するか、上の「既定値で保存」で一括保存してください。
              保存後はいつでも編集・履歴からの復元ができます。
            </p>
          )}
        </>
      )}
    </Card>
  );
}
